// Others.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  Chip,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  IconButton,
  Alert,
  InputAdornment
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';

const Others = ({ onOthersChange, initialOthers = [] }) => {
  const [others, setOthers] = useState(initialOthers);
  const [selectedType, setSelectedType] = useState('');
  const [customDescription, setCustomDescription] = useState('');
  const [amount, setAmount] = useState('');
  const [isCustom, setIsCustom] = useState(false);

  // Predefined other allocation types
  const predefinedTypes = [
    'Lateness Fine',
    'Loan Processing Fees',
    'Advocate Fees',
    'Insurance Risk Fund',
    'Contribution for Deceased',
    'Registration Fee',
    'Meeting Absence Fine',
    'Administrative Fees'
  ];

  // Calculate total others amount
  const getTotalOthersAmount = () => {
    return others.reduce((total, item) => total + (item.amount || 0), 0);
  };

  // Handle adding a new other allocation
  const handleAddOther = () => {
    if (!amount || parseFloat(amount) <= 0) {
      return;
    }

    const description = isCustom ? customDescription.trim() : selectedType;
    
    if (!description) {
      return;
    }

    const newOther = {
      id: Date.now(), // Simple ID generation
      description: description,
      amount: parseFloat(amount),
      isCustom: isCustom
    };

    const updatedOthers = [...others, newOther];
    setOthers(updatedOthers);
    
    // Reset form
    setSelectedType('');
    setCustomDescription('');
    setAmount('');
    setIsCustom(false);
  };

  // Handle removing an other allocation
  const handleRemoveOther = (id) => {
    const updatedOthers = others.filter(item => item.id !== id);
    setOthers(updatedOthers);
  };

  // Handle editing an existing other allocation
  const handleEditOther = (id, newAmount) => {
    const numAmount = parseFloat(newAmount) || 0;
    const updatedOthers = others.map(item => 
      item.id === id ? { ...item, amount: numAmount } : item
    );
    setOthers(updatedOthers);
  };

  // Handle type selection change
  const handleTypeChange = (value) => {
    if (value === 'custom') {
      setIsCustom(true);
      setSelectedType('');
    } else {
      setIsCustom(false);
      setSelectedType(value);
      setCustomDescription('');
    }
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES',
      minimumFractionDigits: 2
    }).format(amount);
  };

  // Validate form inputs
  const isFormValid = () => {
    const hasValidAmount = amount && parseFloat(amount) > 0;
    const hasValidDescription = isCustom ? customDescription.trim() : selectedType;
    return hasValidAmount && hasValidDescription;
  };

  // Notify parent component of changes
  useEffect(() => {
    onOthersChange({
      items: others,
      totalAmount: getTotalOthersAmount(),
      descriptions: others.map(item => `${item.description}: ${formatCurrency(item.amount)}`).join('; ')
    });
  }, [others]);

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Other Allocations
      </Typography>

      {/* Add new other allocation form */}
      <Box sx={{ mb: 3, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
        <Typography variant="subtitle2" gutterBottom>
          Add New Allocation
        </Typography>
        
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>Type</InputLabel>
              <Select
                value={isCustom ? 'custom' : selectedType}
                onChange={(e) => handleTypeChange(e.target.value)}
                label="Type"
              >
                {predefinedTypes.map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
                <MenuItem value="custom">
                  <em>Custom (Other)</em>
                </MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {isCustom && (
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Custom Description"
                value={customDescription}
                onChange={(e) => setCustomDescription(e.target.value)}
                placeholder="Enter custom description"
              />
            </Grid>
          )}

          <Grid item xs={12} md={isCustom ? 2 : 4}>
            <TextField
              fullWidth
              label="Amount"
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              InputProps={{
                startAdornment: <InputAdornment position="start">KES</InputAdornment>,
              }}
            />
          </Grid>

          <Grid item xs={12} md={2}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddOther}
              disabled={!isFormValid()}
              fullWidth
            >
              Add
            </Button>
          </Grid>
        </Grid>
      </Box>

      {/* Display existing other allocations */}
      {others.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Current Allocations ({others.length})
          </Typography>
          
          {others.map((item) => (
            <Box
              key={item.id}
              sx={{
                display: 'flex',
                alignItems: 'center',
                mb: 1,
                p: 1,
                bgcolor: 'grey.50',
                borderRadius: 1
              }}
            >
              <Box sx={{ flexGrow: 1, mr: 2 }}>
                <Typography variant="body2" fontWeight="medium">
                  {item.description}
                  {item.isCustom && (
                    <Chip 
                      label="Custom" 
                      size="small" 
                      color="secondary" 
                      sx={{ ml: 1, height: 20 }}
                    />
                  )}
                </Typography>
              </Box>
              
              <TextField
                size="small"
                type="number"
                value={item.amount || ''}
                onChange={(e) => handleEditOther(item.id, e.target.value)}
                InputProps={{
                  startAdornment: <InputAdornment position="start">KES</InputAdornment>,
                }}
                sx={{ width: 120, mr: 1 }}
              />
              
              <IconButton
                color="error"
                size="small"
                onClick={() => handleRemoveOther(item.id)}
              >
                <DeleteIcon />
              </IconButton>
            </Box>
          ))}

          <Box sx={{ mt: 2, p: 1, bgcolor: 'primary.light', borderRadius: 1 }}>
            <Typography variant="subtitle1" align="right">
              Total Other Allocations: {formatCurrency(getTotalOthersAmount())}
            </Typography>
          </Box>
        </Box>
      )}

      {others.length === 0 && (
        <Alert severity="info" sx={{ mt: 2 }}>
          No other allocations added yet. Use the form above to add allocations for fines, fees, or other purposes.
        </Alert>
      )}
    </Box>
  );
};

export default Others;